import { HistoryOutlined, PlayCircleOutlined } from "@mui/icons-material";
import { Box, Paper, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { JumpingDots } from "../../components/UIToolkit/Animations/JumpingDots";
import MetaChip, { type MetaChipTone } from "../../components/UIToolkit/Badges/MetaChip";
import { EmptyState } from "../../components/UIToolkit/molecules/EmptyState";
import SystemBanner from "../../components/UIToolkit/molecules/SystemBanner";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import type { OnboardingJob, OnboardingJobStatus } from "../../services/backend.service";
import { OnboardingJobProgress } from "./OnboardingJobProgress";
import { useCancelOnboardingJob, useRetryOnboardingJobItem } from "./useOnboardingJobs";

const RECENT_RUN_LIMIT = 6;

const STATUS_TONE: Record<OnboardingJobStatus, MetaChipTone> = {
	queued: "neutral",
	running: "primary",
	succeeded: "success",
	partially_succeeded: "warning",
	failed: "error",
	cancel_requested: "warning",
	cancelled: "neutral",
};

type Props = {
	jobs: OnboardingJob[] | undefined;
	selectedJob: OnboardingJob | undefined;
	timezone: string;
	isLoading: boolean;
	isError: boolean;
	onSelect: (job: OnboardingJob) => void;
	onRerun: (job: OnboardingJob) => void;
};

export function OnboardingRunsPanel({ jobs, selectedJob, timezone, isLoading, isError, onSelect, onRerun }: Props) {
	const { t } = useTranslation();
	const cancelJob = useCancelOnboardingJob();
	const retryItem = useRetryOnboardingJobItem();
	const visibleJobs = jobs?.slice(0, RECENT_RUN_LIMIT);

	return (
		<Paper
			variant="outlined"
			data-testid="onboarding-runs-panel"
			sx={{ p: { xs: 2, md: 2.5 }, display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}
		>
			<Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
				<Box>
					<Typography variant="h6" sx={{ fontWeight: 700 }}>
						{t("onboarding_jobs.panel_title")}
					</Typography>
					<Typography variant="body2" sx={{ color: "text.secondary" }}>
						{t("onboarding_jobs.panel_description")}
					</Typography>
				</Box>
				{jobs && <MetaChip label={t("onboarding_jobs.run_count", { count: jobs.length })} tone="primary" size="small" />}
			</Box>

			{isLoading && (
				<Box data-testid="onboarding-runs-loading" sx={{ display: "flex", justifyContent: "center", py: 2 }}>
					<JumpingDots />
				</Box>
			)}

			{isError && (
				<SystemBanner
					tone="error"
					title={t("onboarding_jobs.errors.list_title")}
					description={t("onboarding_jobs.errors.list_description")}
					dataTestId="onboarding-runs-error"
				/>
			)}

			{visibleJobs?.length === 0 && !isLoading && !isError && (
				<EmptyState
					icon={<HistoryOutlined />}
					title={t("onboarding_jobs.empty_title")}
					hint={t("onboarding_jobs.empty_hint")}
					dataTestId="onboarding-runs-empty"
					sx={{ py: 2 }}
				/>
			)}

			{visibleJobs && visibleJobs.length > 0 && (
				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1 }}>
					{visibleJobs.map((job) => {
						const createdAt = DateTime.fromISO(job.created_at, { setZone: true }).setZone(timezone);
						return (
							<Box
								key={job.id}
								data-testid={`onboarding-run-summary-${job.id}`}
								data-selected={selectedJob?.id === job.id}
								sx={(theme) => ({
									display: "flex",
									alignItems: "center",
									gap: 1.5,
									p: 1.5,
									borderRadius: 1,
									border: `1px solid ${selectedJob?.id === job.id ? theme.palette.primary.main : theme.palette.divider}`,
									backgroundColor: selectedJob?.id === job.id ? theme.palette.action.selected : theme.palette.background.paper,
								})}
							>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
										{t(`onboarding_jobs.type.${job.job_type}`)}
									</Typography>
									<Typography variant="caption" sx={{ color: "text.secondary" }}>
										{createdAt.toLocaleString(DateTime.DATETIME_MED)}
									</Typography>
								</Box>
								<MetaChip label={t(`onboarding_jobs.status.${job.status}`)} tone={STATUS_TONE[job.status]} size="small" />
								<SystemButton
									buttonVariant="borderless"
									size="small"
									leadingIcon={<PlayCircleOutlined />}
									onClick={() => onSelect(job)}
									data-testid={`onboarding-run-open-${job.id}`}
								>
									{t("onboarding_jobs.open")}
								</SystemButton>
							</Box>
						);
					})}
				</Box>
			)}

			{selectedJob && (
				<OnboardingJobProgress
					job={selectedJob}
					timezone={timezone}
					compact
					onCancel={() => cancelJob.mutate({ departmentId: selectedJob.department_id, jobId: selectedJob.id })}
					onRetryItem={(itemId) => retryItem.mutate({ departmentId: selectedJob.department_id, jobId: selectedJob.id, itemId })}
					isCancelling={cancelJob.isPending && cancelJob.variables?.jobId === selectedJob.id}
					retryingItemId={retryItem.isPending && retryItem.variables?.jobId === selectedJob.id ? retryItem.variables.itemId : undefined}
					onRerun={() => onRerun(selectedJob)}
				/>
			)}
		</Paper>
	);
}
